import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { AppDispatch } from '../../redux';
import { fetchSimplePracticeClinicians } from '../../redux/clinic/clinicSlice';
import { fetchInvitations } from '../../redux/invitation/invitationSlice';
import { usePaymentMethod } from '../../hooks/Billing/usePaymentMethod';
import { AddPaymentMethodModal } from '../modals/AddPaymentMethodModal';
import {
  CreditCard,
  DollarSign,
  Users,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  FileText,
  Receipt,
} from 'lucide-react';

const listVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.9 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 25,
    },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 120,
      damping: 20,
    },
  },
};

// Cost per clinician for workforce invitations
const COST_PER_CLINICIAN = 59.0;

export const WorkforceBillingSection: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const practiceClinicians = useSelector((state: any) => state?.clinics?.practiceClinicians || []);
  const invitations = useSelector((state: any) => state?.invitation?.invitations || []);
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'invoices'>('overview');
  const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] = useState(false);

  const getUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.sub || user?.['sub'] || undefined;
    } catch {
      return undefined;
    }
  };

  const getCustomerId = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.stripe_customer_id || user?.customerId || undefined;
    } catch {
      return undefined;
    }
  };

  const userId = getUserId();
  const customerId = getCustomerId();
  const { paymentMethods, isLoading: isLoadingPaymentMethods, error: paymentMethodError, fetch: fetchPaymentMethodsList } =
    usePaymentMethod({
      userId,
      customerId,
      autoFetch: true,
    });

  const getBillingHistory = () => {
    if (!invitations || invitations.length === 0) return [];

    // Group invitations by date (YYYY-MM-DD)
    const groups: Record<string, any[]> = {};
    invitations.forEach((inv: any) => {
      if (!inv.createdAt) return;
      const date = inv.createdAt.split('T')[0];
      if (!groups[date]) groups[date] = [];
      groups[date].push(inv);
    });

    return Object.entries(groups)
      .map(([date, items], index) => ({
        id: `inv-batch-${date}-${index}`,
        date,
        type: 'Workforce Invitation',
        description: `Invited ${items.length} clinician${items.length > 1 ? 's' : ''} to join practice`,
        amount: items.length * COST_PER_CLINICIAN,
        status: items.every(inv => inv.status === 'accepted') ? 'completed' : 'pending',
        invoiceId: `INV-${date.replace(/-/g, '')}-${String(index + 1).padStart(3, '0')}`,
        clinicianCount: items.length,
      }))
      .sort((a, b) => b.date.localeCompare(a.date));
  };

  const billingHistory = getBillingHistory();

  const getBillingStats = () => {
    const totalInvited = practiceClinicians.filter((c: any) => Boolean(c?.invite || c?.invited)).length;

    const totalSpent = billingHistory
      .filter((item) => item.status === 'completed')
      .reduce((sum, item) => sum + item.amount, 0);

    const pendingAmount = billingHistory
      .filter((item) => item.status === 'pending')
      .reduce((sum, item) => sum + item.amount, 0);

    const totalInvoices = billingHistory.length;

    return {
      totalInvited,
      totalSpent,
      pendingAmount,
      totalInvoices,
    };
  };

  const stats = getBillingStats();

  useEffect(() => {
    dispatch(fetchSimplePracticeClinicians());
    dispatch(fetchInvitations());
  }, [dispatch]);

  const handlePaymentMethodAdded = async () => {
    setShowAddPaymentMethodModal(false);
    if (userId) {
      await fetchPaymentMethodsList();
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          icon: CheckCircle,
          bgColor: 'bg-emerald-100 text-emerald-700 border-emerald-200',
          text: 'Completed',
        };
      case 'pending':
        return {
          icon: AlertCircle,
          bgColor: 'bg-yellow-100 text-yellow-700 border-yellow-200',
          text: 'Pending',
        };
      case 'failed':
        return {
          icon: AlertCircle,
          bgColor: 'bg-red-100 text-red-700 border-red-200',
          text: 'Failed',
        };
      default:
        return {
          icon: AlertCircle,
          bgColor: 'bg-gray-100 text-gray-700 border-gray-200',
          text: status,
        };
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between mb-8 p-6 backdrop-blur-lg bg-white/45 rounded-3xl border border-white/30 shadow-xl shadow-indigo-500/10"
          >
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
                Workforce Billing
              </h1>
              <p className="text-slate-600">Manage billing for workforce invitations and payments</p>
            </div>
          </motion.div>

          <div className="mb-6">
            <div className="inline-flex rounded-2xl border border-white/40 bg-white/60 backdrop-blur-md p-1 shadow-sm">
              {[
                { id: 'overview', label: 'Overview', icon: TrendingUp },
                { id: 'history', label: 'Payment History', icon: Receipt },
                { id: 'invoices', label: 'Invoices', icon: FileText },
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as 'overview' | 'history' | 'invoices')}
                    className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-colors ${
                      activeTab === tab.id ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <motion.div
            variants={listVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
          >
            {[
              {
                icon: Users,
                value: stats.totalInvited,
                label: 'Total Invited',
                color: 'from-blue-500 to-cyan-600',
                bg: 'from-blue-500/10 to-cyan-500/10',
                description: 'Clinicians invited',
              },
              {
                icon: DollarSign,
                value: formatCurrency(stats.totalSpent),
                label: 'Total Spent',
                color: 'from-emerald-500 to-green-600',
                bg: 'from-emerald-500/10 to-green-500/10',
                description: 'Completed payments',
              },
              {
                icon: AlertCircle,
                value: formatCurrency(stats.pendingAmount),
                label: 'Pending Amount',
                color: 'from-yellow-500 to-orange-600',
                bg: 'from-yellow-500/10 to-orange-500/10',
                description: 'Outstanding payments',
              },
              {
                icon: FileText,
                value: stats.totalInvoices,
                label: 'Total Invoices',
                color: 'from-purple-500 to-violet-600',
                bg: 'from-purple-500/10 to-violet-500/10',
                description: 'Generated invoices',
              },
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className={`backdrop-blur-xl bg-gradient-to-br ${stat.bg} border border-white/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <motion.div
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                      className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center cursor-pointer"
                    >
                      <RefreshCw className="w-4 h-4 text-gray-600" />
                    </motion.div>
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-600 font-medium mb-1">{stat.label}</div>
                  <div className="text-xs text-gray-500">{stat.description}</div>
                </motion.div>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="backdrop-blur-lg bg-white/50 rounded-3xl shadow-2xl border border-white/30 overflow-hidden"
          >
            {activeTab === 'overview' && (
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Billing Overview</h3>
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h4>
                  <div className="space-y-4">
                    {billingHistory.slice(0, 3).map((item) => {
                      const statusBadge = getStatusBadge(item.status);
                      const StatusIcon = statusBadge.icon;
                      return (
                        <motion.div
                          key={item.id}
                          variants={rowVariants}
                          className="backdrop-blur-md bg-white/60 border border-white/40 rounded-2xl p-4 shadow-lg hover:shadow-xl hover:bg-white/70 transition-all duration-300"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold">
                                <Users className="w-6 h-6" />
                              </div>
                              <div>
                                <h5 className="font-semibold text-gray-900">{item.description}</h5>
                                <p className="text-sm text-gray-600">
                                  {formatDate(item.date)} • {item.clinicianCount} clinicians
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <div className="text-lg font-bold text-gray-900">{formatCurrency(item.amount)}</div>
                                <div className="text-sm text-gray-600">Invoice {item.invoiceId}</div>
                              </div>
                              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium border ${statusBadge.bgColor}`}>
                                <StatusIcon className="w-4 h-4" />
                                {statusBadge.text}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowAddPaymentMethodModal(true)}
                      className="flex items-center justify-center gap-2 p-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 transition-all duration-300"
                    >
                      <CreditCard className="w-5 h-5" />
                      Attach Payment Method
                    </motion.button>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Payment Methods</h4>

                  {isLoadingPaymentMethods ? (
                    <div className="p-4 rounded-xl bg-white/50 border border-white/30 text-sm text-gray-600">
                      Loading payment methods...
                    </div>
                  ) : paymentMethodError ? (
                    <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                      {paymentMethodError}
                    </div>
                  ) : paymentMethods.length === 0 ? (
                    <div className="p-4 rounded-xl bg-white/50 border border-white/30 text-sm text-gray-600">
                      No payment methods attached yet.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paymentMethods.map((method) => (
                        <div
                          key={method.id}
                          className="flex items-center justify-between rounded-xl bg-white/60 border border-white/30 p-4"
                        >
                          <div>
                            <p className="text-sm font-semibold text-gray-900 capitalize">
                              {method.brand || method.type} •••• {method.last4}
                            </p>
                            <p className="text-xs text-gray-600">
                              {method.expiryMonth && method.expiryYear
                                ? `Expires ${String(method.expiryMonth).padStart(2, '0')}/${String(method.expiryYear).slice(-2)}`
                                : 'Expiry not available'}
                            </p>
                          </div>
                          {method.isDefault ? (
                            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-indigo-100 text-indigo-700">
                              Default
                            </span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Payment History</h3>
                <div className="space-y-4">
                  {billingHistory.map((item) => {
                    const statusBadge = getStatusBadge(item.status);
                    const StatusIcon = statusBadge.icon;
                    return (
                      <motion.div
                        key={item.id}
                        variants={rowVariants}
                        className="backdrop-blur-sm bg-white/50 border border-white/30 rounded-2xl p-6 shadow-lg hover:shadow-xl hover:bg-white/60 transition-all duration-300"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-6">
                            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white font-bold text-lg">
                              <Receipt className="w-8 h-8" />
                            </div>
                            <div>
                              <h5 className="font-bold text-gray-900 text-lg">{item.description}</h5>
                              <p className="text-gray-600">{formatDate(item.date)}</p>
                              <p className="text-sm text-gray-500">Invoice: {item.invoiceId}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <div className="text-2xl font-bold text-gray-900">{formatCurrency(item.amount)}</div>
                              <div className="text-sm text-gray-600">{item.clinicianCount} clinicians</div>
                            </div>
                            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${statusBadge.bgColor}`}>
                              <StatusIcon className="w-4 h-4" />
                              {statusBadge.text}
                            </div>
                            <button className="p-2 bg-white/60 backdrop-blur-sm rounded-xl text-gray-600 hover:bg-white/80 transition-all duration-200">
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'invoices' && (
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Invoice Management</h3>
                <div className="space-y-4">
                  {billingHistory.map((item) => (
                    <motion.div
                      key={item.id}
                      variants={rowVariants}
                      className="backdrop-blur-sm bg-white/50 border border-white/30 rounded-2xl p-6 shadow-lg hover:shadow-xl hover:bg-white/60 transition-all duration-300"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center text-white font-bold text-lg">
                            <FileText className="w-8 h-8" />
                          </div>
                          <div>
                            <h5 className="font-bold text-gray-900 text-lg">{item.invoiceId}</h5>
                            <p className="text-gray-600">{item.description}</p>
                            <p className="text-sm text-gray-500">Generated: {formatDate(item.date)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(item.amount)}</div>
                            <div className="text-sm text-gray-600">{item.clinicianCount} clinicians</div>
                          </div>
                          <div className="flex gap-2">
                            <button className="p-2 bg-white/60 backdrop-blur-sm rounded-xl text-gray-600 hover:bg-white/80 transition-all duration-200">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="p-2 bg-white/60 backdrop-blur-sm rounded-xl text-gray-600 hover:bg-white/80 transition-all duration-200">
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <AddPaymentMethodModal
        isOpen={showAddPaymentMethodModal}
        onClose={() => setShowAddPaymentMethodModal(false)}
        onSuccess={handlePaymentMethodAdded}
        userId={userId}
        customerId={customerId}
        setAsDefault={paymentMethods.length === 0}
      />
    </div>
  );
};
